// Unified Locale Detection Utility
import { RegionCode } from '../types/region';
import { LanguageCode } from '../types/language';
import { REGION_DEFAULT_LANGUAGES } from './languageData';

interface DetectedLocale {
  region: RegionCode;
  language: LanguageCode;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Detect user's locale (region and language) from browser settings
 * This provides a unified detection for both region and language
 */
export function detectUserLocale(): DetectedLocale {
  try {
    // Get browser locale
    const browserLocale = navigator.language || 'en-US';
    const [langCode, countryCode] = browserLocale.split('-');
    
    console.log('🌍 Browser locale detected:', browserLocale);
    
    // Comprehensive locale to region mapping
    const localeMap: Record<string, { region: RegionCode; language: LanguageCode; confidence: 'high' | 'medium' | 'low' }> = {
      // India
      'en-IN': { region: 'IN', language: 'en', confidence: 'high' },
      'hi-IN': { region: 'IN', language: 'hi', confidence: 'high' },
      'hi': { region: 'IN', language: 'hi', confidence: 'high' },
      
      // United States
      'en-US': { region: 'US', language: 'en', confidence: 'high' },
      'es-US': { region: 'US', language: 'es', confidence: 'high' },
      
      // United Kingdom
      'en-GB': { region: 'GB', language: 'en', confidence: 'high' },
      
      // Europe
      'de-DE': { region: 'EU', language: 'en', confidence: 'high' },
      'de-AT': { region: 'EU', language: 'en', confidence: 'high' },
      'de-CH': { region: 'EU', language: 'en', confidence: 'high' },
      'fr-FR': { region: 'EU', language: 'en', confidence: 'high' },
      'fr-BE': { region: 'EU', language: 'en', confidence: 'high' },
      'fr-CH': { region: 'EU', language: 'en', confidence: 'high' },
      'es-ES': { region: 'EU', language: 'es', confidence: 'high' },
      'it-IT': { region: 'EU', language: 'en', confidence: 'medium' },
      'nl-NL': { region: 'EU', language: 'en', confidence: 'medium' },
      'pt-PT': { region: 'EU', language: 'en', confidence: 'high' },
      'pl-PL': { region: 'EU', language: 'en', confidence: 'medium' },
      
      // Australia
      'en-AU': { region: 'AU', language: 'en', confidence: 'high' },
      
      // Canada
      'en-CA': { region: 'CA', language: 'en', confidence: 'high' },
      'fr-CA': { region: 'CA', language: 'en', confidence: 'high' },
      
      // Singapore
      'en-SG': { region: 'SG', language: 'en', confidence: 'high' },
      'zh-SG': { region: 'SG', language: 'en', confidence: 'high' },
      
      // UAE
      'ar-AE': { region: 'AE', language: 'ar', confidence: 'high' },
      'en-AE': { region: 'AE', language: 'en', confidence: 'high' },
      
      // Malaysia
      'en-MY': { region: 'MY', language: 'en', confidence: 'high' },
      'ms-MY': { region: 'MY', language: 'en', confidence: 'medium' },
      'zh-MY': { region: 'MY', language: 'en', confidence: 'high' },
    };
    
    // Try exact match first
    if (localeMap[browserLocale]) {
      console.log('✅ Exact locale match:', localeMap[browserLocale]);
      return localeMap[browserLocale];
    }
    
    // Try language-only match with region inference
    const languageOnlyMap: Record<string, { region: RegionCode; language: LanguageCode; confidence: 'medium' | 'low' }> = {
      'de': { region: 'EU', language: 'en', confidence: 'medium' },
      'fr': { region: 'EU', language: 'en', confidence: 'medium' },
      'es': { region: 'US', language: 'es', confidence: 'medium' },
      'pt': { region: 'EU', language: 'en', confidence: 'medium' },
      'zh': { region: 'SG', language: 'en', confidence: 'medium' },
      'ar': { region: 'AE', language: 'ar', confidence: 'medium' },
      'ja': { region: 'GLOBAL', language: 'en', confidence: 'low' },
      'ko': { region: 'GLOBAL', language: 'en', confidence: 'low' },
    };
    
    if (languageOnlyMap[langCode]) {
      console.log('✅ Language-only match:', languageOnlyMap[langCode]);
      return languageOnlyMap[langCode];
    }
    
    // Try timezone-based detection as fallback
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneMap: Record<string, RegionCode> = {
      'Asia/Kolkata': 'IN',
      'Asia/Calcutta': 'IN',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'Europe/London': 'GB',
      'Europe/Paris': 'EU',
      'Europe/Berlin': 'EU',
      'Europe/Madrid': 'EU',
      'Europe/Rome': 'EU',
      'Europe/Amsterdam': 'EU',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'America/Toronto': 'CA',
      'America/Vancouver': 'CA',
      'Asia/Singapore': 'SG',
      'Asia/Dubai': 'AE',
      'Asia/Kuala_Lumpur': 'MY',
    };
    
    if (timezoneMap[timezone]) {
      const detectedRegion = timezoneMap[timezone];
      const defaultLanguage = REGION_DEFAULT_LANGUAGES[detectedRegion] || 'en';
      console.log('✅ Timezone-based detection:', { region: detectedRegion, language: defaultLanguage });
      return {
        region: detectedRegion,
        language: defaultLanguage,
        confidence: 'low'
      };
    }
    
    // Default fallback - use browser language if supported, otherwise English
    const supportedLanguages: LanguageCode[] = ['en', 'hi', 'es', 'ar'];
    const fallbackLanguage = supportedLanguages.includes(langCode as LanguageCode) 
      ? (langCode as LanguageCode) 
      : 'en';
    
    console.log('⚠️ Using fallback detection');
    return {
      region: 'GLOBAL',
      language: fallbackLanguage,
      confidence: 'low'
    };
    
  } catch (error) {
    console.error('Error detecting locale:', error);
    return {
      region: 'GLOBAL',
      language: 'en',
      confidence: 'low'
    };
  }
}

/**
 * Check if locale has been manually set by user
 */
export function hasUserSetLocale(): boolean {
  try {
    const hasRegion = localStorage.getItem('trainzilla_region_preference') !== null;
    const hasLanguage = localStorage.getItem('trainzilla_language_preference') !== null;
    return hasRegion && hasLanguage;
  } catch {
    return false;
  }
}

/**
 * Save user's locale preference
 */
export function saveLocalePreference(region: RegionCode, language: LanguageCode): void {
  try {
    localStorage.setItem('trainzilla_region_preference', region);
    localStorage.setItem('trainzilla_language_preference', language);
    localStorage.setItem('trainzilla_locale_auto_detected', 'false');
  } catch (error) {
    console.error('Error saving locale preference:', error);
  }
}

/**
 * Get saved locale preference
 */
export function getSavedLocale(): { region: RegionCode; language: LanguageCode } | null {
  try {
    const savedRegion = localStorage.getItem('trainzilla_region_preference');
    const savedLanguage = localStorage.getItem('trainzilla_language_preference');
    
    if (savedRegion && savedLanguage) {
      return {
        region: savedRegion as RegionCode,
        language: savedLanguage as LanguageCode
      };
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Detect user's country code from IP using a free API (ipapi.co)
 * This is more accurate than browser settings for geography-based i18n
 */
export async function detectUserGeography(): Promise<RegionCode | null> {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) return null;
    
    const data = await response.json();
    const countryCode = data.country_code;
    try {
      if (countryCode) localStorage.setItem('trainzilla_country', String(countryCode));
    } catch {
      /* private mode */
    }
    
    console.log('🌍 IP-based geography detected:', countryCode);
    
    // Map detected country code to our supported RegionCode
    const supportedRegionMap: Record<string, RegionCode> = {
      'IN': 'IN',
      'US': 'US',
      'GB': 'GB',
      'IE': 'EU', 'FR': 'EU', 'DE': 'EU', 'ES': 'EU', 'IT': 'EU', 'NL': 'EU', 'PT': 'EU', 'PL': 'EU',
      'AU': 'AU',
      'CA': 'CA',
      'SG': 'SG',
      'AE': 'AE',
      'MY': 'MY'
    };
    
    return supportedRegionMap[countryCode] || 'GLOBAL';
  } catch (error) {
    console.warn('⚠️ GEO detection failed, falling back to browser settings:', error);
    return null;
  }
}

/**
 * Initialize locale - either from saved preference or auto-detect
 */
export async function initializeLocale(): Promise<{ region: RegionCode; language: LanguageCode; source: 'saved' | 'detected' | 'geo' }> {
  // 1. Check for saved preference first
  const saved = getSavedLocale();
  if (saved) {
    console.log('📍 Locale loaded from saved preferences:', saved);
    return { ...saved, source: 'saved' };
  }
  
  // 2. Try IP-based geography detection first (Most accurate)
  const geoRegion = await detectUserGeography();
  if (geoRegion) {
    const defaultLanguage = REGION_DEFAULT_LANGUAGES[geoRegion] || 'en';
    console.log('📍 Locale auto-detected via GEO:', { region: geoRegion, language: defaultLanguage });
    
    // Save as preference so we don't fetch every time
    saveLocalePreference(geoRegion, defaultLanguage);
    localStorage.setItem('trainzilla_locale_auto_detected', 'true');
    localStorage.setItem('trainzilla_locale_detection_source', 'geo');
    
    return {
      region: geoRegion,
      language: defaultLanguage,
      source: 'geo'
    };
  }
  
  // 3. Fallback to browser settings/timezone if GEO fails
  const detected = detectUserLocale();
  console.log('📍 Locale auto-detected via Browser:', detected);
  
  // Save the auto-detected locale
  saveLocalePreference(detected.region, detected.language);
  localStorage.setItem('trainzilla_locale_auto_detected', 'true');
  localStorage.setItem('trainzilla_locale_detection_source', 'browser');
  
  return {
    region: detected.region,
    language: detected.language,
    source: 'detected'
  };
}

export default {
  detectUserGeography,
  detectUserLocale,
  hasUserSetLocale,
  saveLocalePreference,
  getSavedLocale,
  initializeLocale
};
